import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";

import RecognitionDialog from "../RecognitionDialog";
import RecognitionBanner from "../RecognitionBanner";
import RecognitionDirectAtCard from "../RecognitionDirectAtCard";

import "./RecognitionHome.css";

import MyPeer from "../../assets/images/my-peer.png";
import Supervisor from "../../assets/images/supervisor.png";
import DirectReports from "../../assets/images/direct-reports.png";

import { DIRECTED_AT } from "../../constants/constants";
import { ERROR_CODE } from "../../../../constants/constants";

import {
  fetchCategories,
  postRecognition,
  fetchMySupervisor,
} from "../../services/recognition";

import { isEmpty as isArrayEmpty } from "../../../../utils/array";
import { isEmpty as isObjectEmpty } from "../../../../utils/object";
import { isEmpty as isStringEmpty } from "../../../../utils/string";

export default function RecognitionHome() {
  const navigate = useNavigate();

  const [
    hasTriedRecognitionFormSubmission,
    setHasTriedRecognitionFormSubmission,
  ] = useState(false);
  const [categories, setCategories] = useState([]);
  const [mySupervisor, setMySupervisor] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [directedAtForm, setDirectedAtForm] = useState({
    directedAt: DIRECTED_AT["My Supervisor"].value,
    category: "",
    reason: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [isFetchingAgencyCategories, setIsFetchingAgencyCategories] =
    useState(false);
  const [isSubmittingRecognitionForm, setIsSubmittingRecognitionForm] =
    useState(false);
  const [isErrorMessageShown, setIsErrorMessageShown] = useState(false);
  const [isSuccessMessageShown, setIsSuccessMessageShown] = useState(false);
  const [isFetchingMySupervisor, setIsFetchingMySupervisor] = useState(false);
  const [isRecognitionDialogOpen, setIsRecognitionDialogOpen] = useState(false);

  /**
   * Method to handle the closing of the Recognition dialog.
   */
  const handleRecognitionDialogClose = () => {
    setHasTriedRecognitionFormSubmission(false);
    setDirectedAtForm({
      directedAt: DIRECTED_AT["My Supervisor"].value,
      category: categories?.[0],
      reason: "",
    });
    closeRecognitionDialog();
  };

  /**
   * Closes the dialog to provide Recognition.
   */
  const closeRecognitionDialog = () => {
    setIsRecognitionDialogOpen(false);
  };

  /**
   * Opens the dialog to provide Recognition.
   */
  const openRecognitionDialog = () => {
    setIsRecognitionDialogOpen(true);
  };

  /**
   * Handles when clicked on the My Supervisor card.
   */
  const handleDirectAtSupervisorCardClick = () => {
    openRecognitionDialog();

    if (isObjectEmpty(mySupervisor)) {
      handleFetchMySupervisor();
    }

    if (isArrayEmpty(categories)) {
      handleFetchAgencyCategories();
    }
  };

  /**
   * Fetches the Agency Categories via API call.
   *
   * @returns {Promise}
   */
  const fetchAgencyCategories = async () => {
    try {
      return await fetchCategories();
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/recognition");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Handles the fetching of the logged in user's Agency Category.
   */
  const handleFetchAgencyCategories = async () => {
    setIsFetchingAgencyCategories(true);
    const response = await fetchAgencyCategories();

    setIsFetchingAgencyCategories(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching Categories: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);
      setCategories([]);

      return;
    }

    setCategories(response?.result?.categories);
    handleRecognitionFormFieldChange(
      "category",
      response?.result?.categories?.[0]
    );
  };

  /**
   * Handles the fetching of the logged in user's Supervisor.
   */
  const handleFetchMySupervisor = async () => {
    setIsFetchingMySupervisor(true);
    const response = await fetchMySupervisorAsync();

    setIsFetchingMySupervisor(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching My Supervisor: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);
      setMySupervisor({});

      return;
    }

    setMySupervisor(response?.result?.employees?.[0]);
  };

  /**
   * Fetches the logged in user's Supervisor via API call.
   *
   * @returns {Promise}
   */
  const fetchMySupervisorAsync = async () => {
    try {
      return await fetchMySupervisor();
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/recognition");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Renders the error snackbar.
   *
   * @returns {Component}
   */
  const renderErrorSnackbar = () => (
    <Snackbar
      open={isErrorMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsErrorMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsErrorMessageShown(false)}
        severity="error"
        sx={{ width: "100%" }}
      >
        {errorMessage}
      </MuiAlert>
    </Snackbar>
  );

  /**
   * Renders the success snackbar for the Dashboard page.
   *
   * @returns {Component}
   */
  const renderSuccessSnackbar = () => (
    <Snackbar
      open={isSuccessMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsSuccessMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsSuccessMessageShown(false)}
        severity="success"
        sx={{ width: "100%" }}
      >
        {successMessage}
      </MuiAlert>
    </Snackbar>
  );

  /**
   * Handles the change in the form field of the Recognition Form.
   */
  const handleRecognitionFormFieldChange = (key, value) => {
    setDirectedAtForm({
      ...directedAtForm,
      [key]: value,
    });
  };

  /**
   * Method to check if the Recognition Dialog Form Fields are valid.
   *
   * @returns {Boolean}
   */
  const isRecognitionFormValid = () => {
    return (
      !isStringEmpty(directedAtForm?.directedAt) &&
      !isStringEmpty(directedAtForm?.category) &&
      !isStringEmpty(directedAtForm?.reason)
    );
  };

  /**
   * Submits the Recognition via API calls.
   *
   * @param {Object} payload
   * @returns {Promise}
   */
  const submitRecognitionFormAsync = async (payload = {}) => {
    try {
      return await postRecognition(payload);
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/recognition");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Handles the submission of the Recognition form.
   *
   * @returns {Promise}
   */
  const handleRecognitionSubmit = async () => {
    setHasTriedRecognitionFormSubmission(true);

    if (!isRecognitionFormValid()) {
      return;
    }

    setIsSubmittingRecognitionForm(true);
    const response = await submitRecognitionFormAsync({
      recognized_id: mySupervisor?.id,
      reason: directedAtForm?.reason,
      directed_at: directedAtForm?.directedAt,
      category: directedAtForm?.category,
    });

    setIsSubmittingRecognitionForm(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while submitting Recognition: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);

      return;
    }

    setSuccessMessage(`Successfully submitted the Recognition.`);
    setIsSuccessMessageShown(true);
    handleRecognitionDialogClose();
  };

  return (
    <>
      <div className="recognition-home-main-outer-wrapper">
        <RecognitionBanner />

        <div className="direct-at-card-outer-wrapper">
          <span>Select whom you want to direct your recognition to.</span>
          <div className="direct-at-card-wrapper">
            <div className="direct-at-card">
              <RecognitionDirectAtCard
                avatarImage={Supervisor}
                title={"My Supervisor"}
                onClick={handleDirectAtSupervisorCardClick}
              />
            </div>

            <div className="direct-at-card">
              <RecognitionDirectAtCard
                avatarImage={DirectReports}
                title={"My Direct Reports"}
                onClick={() =>
                  navigate(
                    `/recognition/search-employees?directed-at=${DIRECTED_AT["My Direct Reports"].label}`
                  )
                }
              />
            </div>

            <div className="direct-at-card">
              <RecognitionDirectAtCard
                avatarImage={MyPeer}
                title={"My Teammates"}
                onClick={() =>
                  navigate(
                    `/recognition/search-employees?directed-at=${DIRECTED_AT["My Teammates"].label}`
                  )
                }
              />
            </div>
          </div>
        </div>
      </div>
      <RecognitionDialog
        categories={categories}
        directedAt={mySupervisor}
        formData={directedAtForm}
        open={isRecognitionDialogOpen}
        onSubmit={handleRecognitionSubmit}
        onClose={handleRecognitionDialogClose}
        isSubmitting={isSubmittingRecognitionForm}
        isLoadingDirectedAt={isFetchingMySupervisor}
        isFetchingCategories={isFetchingAgencyCategories}
        changeFormData={handleRecognitionFormFieldChange}
        hasTriedFormSubmission={hasTriedRecognitionFormSubmission}
      />

      {renderErrorSnackbar()}
      {renderSuccessSnackbar()}
    </>
  );
}
